import { useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'

/**
 * Hook para verificar se o usuário atual é GESTOR do setor logado
 * 
 * Diferente do hook useIsAutorizador que verifica a tabela autorizadores,
 * este hook verifica se o usuário tem a função GESTOR no setor logado atual
 * através da relação usuarios_setores (pivot.funcao === 'GESTOR')
 * 
 * Utiliza os dados retornados pelo endpoint /api/user que já contém:
 * - user.setor_logado: objeto do setor logado
 * - user.setor_logado_fk: ID do setor logado
 * - user.setores[].pivot.funcao: função no setor ('GESTOR' ou 'USUARIO')
 * - user.funcao_usuario: função global ('ADMIN', 'DIRETOR', 'USUARIO')
 * 
 * @returns {Object} { isGestor, isAdmin, setor, user, isLoading }
 */
export const useIsGestorDoSetorLogado = () => {
  const { user } = useAuth()

  const result = useMemo(() => {
    if (!user || !user.setor_logado_fk) {
      return { 
        isGestor: false,
        isAdmin: false,
        setor: null 
      }
    }

    // Verificar se o usuário é ADMIN (tem permissão total)
    const isAdmin = user.funcao_usuario === 'ADMIN'
    
    if (isAdmin) {
      return { 
        isGestor: true, // Admin tem permissões de gestor
        isAdmin: true,
        setor: user.setor_logado || user.setores?.find(s => s.id === user.setor_logado_fk) || null
      }
    }

    // Buscar o setor logado na lista de setores do usuário
    // O backend já retorna os setores com o pivot preenchido
    // NORMALIZAÇÃO: Converte 0/1 para false/true usando Boolean()
    const setorLogado = user.setores?.find(
      s => s.id === user.setor_logado_fk && Boolean(s.pivot?.active)
    )

    if (!setorLogado) {
      console.warn('⚠️ [useIsGestorDoSetorLogado] Setor logado não encontrado na lista de setores', {
        userId: user.id,
        setorLogadoId: user.setor_logado_fk,
        setoresDisponiveis: user.setores?.map(s => ({ id: s.id, nome: s.nome_setor }))
      })
      
      return { 
        isGestor: false,
        isAdmin: false,
        setor: null 
      }
    }

    // Verificar se tem função GESTOR no setor logado através do pivot
    const isGestor = setorLogado.pivot?.funcao === 'GESTOR'
    return { 
      isGestor, 
      isAdmin: false,
      setor: setorLogado
    }
  }, [user])

  return { 
    ...result,
    user,
    isLoading: !user,
  }
}
